import {throwError as observableThrowError,  Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';

import { ApiGatewayHelperService } from './../../app/services/apigateway-helper.service';

import { Toc } from './../interfaces/toc';
import { AppHelperService } from './../../app/services/app-helper.service';
import { GroupHelperService } from './groups-helper.service';
import { ProductViewHelperService } from './product-view-helper.service';
import { AuthService } from './../../app/services/auth.service';
import { ProductContentHelper, ContentHelper } from 'libs-content-helper';
import { ContentAccessHelperService } from './content-access-helper.service';
import { ProductTocService } from './product-toc.service';

@Injectable()
export class ProductViewDataService  {
    private productContentHelper: any;
    private productCode: string;
    accessRules: any = {};
    
    constructor(
        private http: HttpClient, 
        private apiGatewayHelperService: ApiGatewayHelperService,
        private appHelperService : AppHelperService,
        private groupHelperService : GroupHelperService,
        private productViewHelperService : ProductViewHelperService,
        private contentAccessHelperService : ContentAccessHelperService,
        private authService: AuthService,
        private productTocService: ProductTocService ){}
    
    //Temp Commenting cache code due to productive skill evaluation teacher updates 

    //Get the shared class/product data (if not available fetch using API)
    /*getClassOrProductViewData(productCode, classId?): Observable<any> {
        var cachedClassOrProductView;
        cachedClassOrProductView  = this.classOrProductViewCache.get('product' + ':' +  productCode);
        
        if (cachedClassOrProductView) {
            // if `sharedClassProductViewData` is available just return it as `Observable`
            return Observable.of(cachedClassOrProductView);
        }
        return this.fetchAndStoreClassProductViewData(productCode, classId).share();
    }*/

    // Get Class/Product data from API
    /** 1. Class Product Scenario : Returns Class + Product Meta data and TOC
     *  2. Only Product Scenario : Returns Product Meta data and TOC
     * 
     */

    //Fetch the class/product data from API and store in sharedClassProductViewData
    fetchAndStoreClassProductViewData(productCode, {classId, assignmentId, unitCode, activityId, accessToExpiredProduct = false, lgid = undefined }): Observable<any> {
        let url = this.apiGatewayHelperService.getApiGatewayPrefixUrl(true) + productCode;
        let queryParams = {};
        if (classId) {
            url += '/' + classId;
            if(assignmentId) {
                url += '/' + assignmentId;
            } else if (unitCode) {
                queryParams['unitCode'] = unitCode;
            }
        }
        //adding lgid in query params
        if (typeof lgid === 'string') {
            queryParams['lgid'] = lgid;
        }
        if (accessToExpiredProduct) {
            queryParams['accessToExpiredProduct'] = accessToExpiredProduct;
        }
        if (queryParams && Object.keys(queryParams).length > 0) {
            url += '?' + Object.keys(queryParams).map(key => `${key}=${queryParams[key]}`).join('&');
        }
        return this.http.get(url)
            .pipe(
                map( (res:any) => {
                    if (res && res.success === false) {
                        return res;
                    }

                    res.groupData? this.groupHelperService.setGroups(res.groupData.entities):'';
                    const response = this.transformTOC(res, { unitCode, activityId, assignmentId });
                    this.updateFolderLevelData(res.toc.tocArray);
                    return response;
                }
            ), catchError((error) => {
                this.appHelperService.navigateToDashboardApp([]);
                return observableThrowError(error);
            }));
    }

    fetchAndStoreProductViewData({ productCode, classId, assignmentId = undefined, learnerDlsId = undefined, unitCode = undefined, activityId = undefined, accessToExpiredProduct = false, fetchClassDetails = true, lgid = undefined}, assignmentDataRequired = false, getLearnerUA = false, rulesDataRequired: any = false, gradebookDataRequired: any = false, ltik = undefined, isEditAssignment = false): Observable<any> {
        let url;
        url = this.apiGatewayHelperService.getApiGatewayPrefixUrl(true) + 'teacher/' + productCode;
        
        if(classId) {
            url += `/${classId}`;
        }
        if(assignmentId) {
            url += `/${assignmentId}`;
        }
        let queryParams = {};
        if(assignmentDataRequired) {
            queryParams['assignmentDataRequired'] = true;
        }
        if (learnerDlsId) {
            queryParams['learnerDlsId'] = learnerDlsId;
        }
        if (getLearnerUA) {
            queryParams['getLearnerUA'] = getLearnerUA;
        }
        if (accessToExpiredProduct) {
            queryParams['accessToExpiredProduct'] = accessToExpiredProduct;
        }
        if(rulesDataRequired){
            queryParams['rulesDataRequired'] = rulesDataRequired;
        }
        if (gradebookDataRequired) {
            queryParams['gradebookDataRequired'] = gradebookDataRequired;
        }
        if (fetchClassDetails) {
            queryParams['fetchClassDetails'] = fetchClassDetails;
        }
        if (ltik) {
            queryParams['ltik'] = ltik;
        }
        if (isEditAssignment) {
            queryParams['isEditAssignment'] = isEditAssignment;
        }
        //adding lgid in query params
        if (typeof lgid === 'string') {
            queryParams['lgid'] = lgid;
        }
        if(queryParams && Object.keys(queryParams).length > 0) {
            url += '?' + Object.keys(queryParams).map(key => `${key}=${queryParams[key]}`).join('&');
        }
        return this.http.get(url).pipe(
            map((res: any) => {
                if (res && res.success === false) {
                    return res;
                }

                // assignmentDataRequired is true when teacher is creating assignments,
                // so we can use it to hide teacher activities while creating assignments
                return this.transformTOC(res, { prepareTocForAssignments: assignmentDataRequired || rulesDataRequired || gradebookDataRequired, unitCode, activityId, assignmentId, isEditAssignment });
            }
        ), catchError((error) => {
            this.appHelperService.navigateToDashboardApp([]);
            return observableThrowError(error);
        }));
    }

    fetchAndStoreProductPreviewData(productCode, stage?): Observable<any> {
        let url = this.apiGatewayHelperService.getApiGatewayPrefixUrl(false) + 'preview/product/' + productCode;
        //Preview stage in case of preview launched via builder
        if(stage) {
            url += '?stage=' + stage;
        }
        return this.http.get(url).pipe(
            map(res => {
                return this.transformTOC(res);
            }
        ), catchError((error) => {
            this.appHelperService.navigateToDashboardApp([]);
            return observableThrowError(error);
        }));
    }

    // Update folder-level data with analytics information
    updateFolderLevelData(tocArray: any) {
        // Recursive function to process each node in the TOC
        function processNode(node: any, lesson: any = null, unit: any = null) {
            // Only initialize if it's a folder
            if (node['item-type'] === 'folder') {
                node.__analytics.itemCounts.completedWithSuccessTestAndContributeCompletion = 0;
    
                // Recursively process children
                node.items?.forEach((child: any) => {
                    processNode(child, node, lesson);
                });
            }
    
            // Process resource-type nodes
            if (node['item-type'] === 'resource') {
                const isTestItem = ContentHelper.isTestContent(node);
                if (isTestItem && node.__analytics.completedOnceWithSuccess) {
                    if (lesson) {
                        lesson.__analytics.itemCounts.completedWithSuccessTestAndContributeCompletion++;
                    }
                    if (unit) {
                        unit.__analytics.itemCounts.completedWithSuccessTestAndContributeCompletion++;
                    }
                }
            }
        }
    
        // Start processing from the parent
        tocArray.result.forEach((unit: any) => processNode(unit));
    }

    //Transfrom the Toc from Array to Object
    /*
     For transforming TOC we have copied the references in tocObject, tocArray , navigationMeta Array.
     This is done to sync all the data between these data Objects i.e if data is updated in tocObject
     it will be updated in tocArray , navigationMeta as well. The cached response in ProductViewDataService
     service is also updated if the data is changed in component.
     */
    transformTOC(classProductViewData, { prepareTocForAssignments = false, unitCode = '', activityId = '', assignmentId = '', isEditAssignment = false } = {}) {
        const targetGrade = classProductViewData.associatedGradingScale?.grades.find((grade)=>{
            return grade.target;
        });
        let targetScore = undefined;
        if (targetGrade) {
            targetScore = targetGrade.min_threshold/100;
        }
        this.productCode = classProductViewData.meta['productcode'];
        const scoreType = classProductViewData.classmeta?.analytics_score_aggregation_type === 'first' ? 'firstResult' : 'bestResult';

        /* userData is needed to determine the user role and launch type */
        const userData = this.authService.getUserData();

        /* Fetching bundleProduct to initialize the library functions */
        if( classProductViewData?.bundleDetails) {
            const bundleProduct = classProductViewData.bundleDetails.products.find( (bP) => bP['dls-product']['code'] === this.productCode);
            this.productContentHelper = new ProductContentHelper(bundleProduct, this.productViewHelperService.isTestTypeComponent(bundleProduct), targetScore, scoreType);

            // For LTI classes, skip setting the assignment-only product flag as a temp hotfix - Need proper implementation later
            if (userData?.launchType !== 'lti') {
                this.productViewHelperService.setIsAssignmentOnlyProduct(bundleProduct.activation?.gated?.student?.['assignments-only']);
            }
        }

        if (classProductViewData.classmeta && userData && userData.role === 'student') {
            this.contentAccessHelperService.setContentAccessEnabled(true);
            const serverTime = classProductViewData.serverTime;
            this.contentAccessHelperService.setCurrentTime(serverTime);
            this.contentAccessHelperService.setClassStartDate(classProductViewData.classmeta.class.startdate);
            this.contentAccessHelperService.setClassEndDate(classProductViewData.classmeta.class.enddate);

            if(classProductViewData.userAssignmentDetails && classProductViewData.userAssignmentDetails.entities) {
                const activeAssignmentDetails: any = {}; // map of itemcode and active assignment start & due date i.e {item-code: startDate, dueDate}
                const scheduledAssignmentDetails: any = {}; // map of itemcode and scheduled assignment start & due date i.e {item-code: startDate, dueDate}
                classProductViewData.userAssignmentDetails.entities.forEach(assignment => {
                    if (!this.productViewHelperService.getIsAssignmentOnlyProduct() && assignment.duedate < this.contentAccessHelperService.getCurrentTime()) {
                        // In case of non-assignment-only products, skip assignments whose due date has passed
                        return;
                    }

                    for(const item of assignment.items) {
                        if (item.dls_product_code === classProductViewData.meta['productcode']) {
                            // Store the scheduled assignment details
                            if (assignment.startdate > this.contentAccessHelperService.getCurrentTime()) {
                                this.addAssignmentDatesToItemsMap(scheduledAssignmentDetails, item['item-code'], assignment.startdate, assignment.duedate);
                            } else if (assignment.duedate >= this.contentAccessHelperService.getCurrentTime()) {
                                // Store the active assignment details
                                this.addAssignmentDatesToItemsMap(activeAssignmentDetails, item['item-code'], assignment.startdate, assignment.duedate);
                            }

                            // For an assignment-only product, update the default lock rule details.
                            this.productViewHelperService.getIsAssignmentOnlyProduct() && this.contentAccessHelperService.updateAssignOnlyItemLockRule(item['item-code'], assignment.startdate);
                        } else {
                            break;
                        }
                    }
                });
                // Set product assignments details in the content access service
                this.contentAccessHelperService.setAssignmentDetails(activeAssignmentDetails, scheduledAssignmentDetails);
            }
    
            if(classProductViewData.accessRuleDetails) {
                const accessRules = classProductViewData.accessRuleDetails.entities;
                if (accessRules.length !== 0) this.accessRules = accessRules[0];
            }
        }

        let toc : Toc = {
            'tocObject' : {},
            'tocArray': classProductViewData["toc"],
            'navigationMeta' : [],
        };

        let tocItemObject ={};

        const productLevelRules = this.contentAccessHelperService.collectLockUnlockRules(this.productCode, null, this.productCode, this.accessRules, this.productViewHelperService.getIsAssignmentOnlyProduct());

        const assignmentIdsMap = {};
        if(assignmentId) {
            classProductViewData.assignmentData = userData?.role === 'student'
                    ? classProductViewData.assignmentData.entities[0] 
                    : classProductViewData.assignmentData;

            if(!isEditAssignment) {
                for (const item of classProductViewData.assignmentData.items) {
                    // Filter out invalid items
                    if(!item.invalid) {
                        const itemCode = item['item-code'];
                        let acc = '';
                        for (let id of itemCode.split('/')) {
                            acc = acc ? acc + '/' + id : id;
                            assignmentIdsMap[acc] = 1;
                        }
                    }
                }
            }
        }

        toc.tocArray.result = toc.tocArray.result.filter((obj, index) => {
            // Set sequence for Parent/Root/Unit Node only
            obj.sequence = index + 1;
            if (unitCode && obj['item-code'] !== unitCode) {
                return false;
            }
            // Do not add unit if not in assignment
            if(assignmentIdsMap && Object.keys(assignmentIdsMap).length > 0 && !assignmentIdsMap.hasOwnProperty(obj['item-code'])) {
                return false;
            }
            tocItemObject = this.transformItem(obj, null, toc['navigationMeta'], productLevelRules, assignmentIdsMap, prepareTocForAssignments || (userData && userData.role === 'student'));
            tocItemObject[obj['item-code']].index = index;
            toc['tocObject'][obj['item-code']] = tocItemObject[obj['item-code']];
            return true;
        });
        // If activityId is at Lesson level or inside lesson level
        if(unitCode && activityId && unitCode !== activityId) {
            let [unitid,lessonid, activityid] = activityId.split('/');
            let activityFound;
            if(!activityid) {
                // if activity is at lesson level(2nd level)
                activityFound = toc.tocArray.result[0].items.find((lessonItem)=> {
                    return lessonItem['item-code'] === activityId;
                })
            } else {
                // if activity is inside lesson level(3rd level)
                let lessonFound = toc.tocArray.result[0].items.find((lessonItem)=> {
                    return lessonItem['item-code'] === unitid + '/' + lessonid;
                })
                activityFound = lessonFound.items.find((activityItem) => {
                    return activityItem['item-code'] === unitid + '/' + lessonid + '/' + activityid;
                })
            }
            // push only the activityFound found in the tocArray.result's 0th index since only one entry is to be displayed in the TOC
            toc.tocArray.result[0] = activityFound;
        }
        this.productViewHelperService.setNavigationMeta(toc.navigationMeta);
        classProductViewData.toc = toc;
        return classProductViewData;
    }
    //Transfrom particular Toc Item from Array to Object
    transformItem(object, parent, tocNavigationMeta, parentLockUnlockRules, assignmentIdsMap, isLearner = false) {
        // Do not add lesson if not in assignment
        if(assignmentIdsMap && Object.keys(assignmentIdsMap).length > 0 && !assignmentIdsMap.hasOwnProperty(object['item-code'])) {
            return false;
        }
        object['accessInfo'] = {
            rules: { lockRules: [], unlockRules: [] },
            totalChildCount: 0,
            childLockCount: 0
        }

        let transformedOject = {};

        transformedOject[object['item-code']] = {};
        Object.keys(object).forEach((prop) => {
            transformedOject[object['item-code']][prop] = object[prop];
        });
        transformedOject[object['item-code']]['parent-name'] = parent;

        if(this.contentAccessHelperService.getContentAccessEnabled()) {
            // Collect current item lock and unlock rules
            object.accessInfo.rules = this.contentAccessHelperService.collectLockUnlockRules(object['item-code'], object['item-type'], this.productCode, this.accessRules, this.productViewHelperService.getIsAssignmentOnlyProduct(), parentLockUnlockRules);
            transformedOject[object['item-code']].accessInfo.rules = object.accessInfo.rules;
        }

        if (object.items) {
            transformedOject[object['item-code']].items = {};
            object.items = object.items.filter((item, index) => {
                // Filter teacher only activity if student 
                if ((item['item-type'] === 'resource') && (isLearner && ContentHelper.isTeacherOnlyContent(item))) {
                    return false;
                }
                // Do not add activity if not in assignment
                if(assignmentIdsMap && Object.keys(assignmentIdsMap).length > 0 && !assignmentIdsMap.hasOwnProperty(item['item-code'])) {
                    return false;
                }
                const parentName = (transformedOject[object['item-code']]['parent-name'] ? transformedOject[object['item-code']]['parent-name'] + ' > ' : '') + object.name;
                transformedOject[object['item-code']].items[item['item-code']] = this.transformItem(item, parentName, tocNavigationMeta, object.accessInfo.rules, assignmentIdsMap, isLearner)[item['item-code']];
                transformedOject[object['item-code']].items[item['item-code']].index = index;

                if(this.contentAccessHelperService.getContentAccessEnabled()) {
                    object['accessInfo']['totalChildCount'] += item['item-type'] === 'resource' ? 1 : object.items[index]['accessInfo']['totalChildCount'];
                    object['accessInfo']['childLockCount'] += item['item-type'] === 'resource'? +object.items[index]['accessInfo']['isLocked'] : object.items[index]['accessInfo']['childLockCount'];
                }
                return true;
            });
        }

        if (transformedOject[object['item-code']]['item-type'] === 'resource') {
            tocNavigationMeta.push({
                'item-code' : transformedOject[object['item-code']]['item-code'],
                '__analytics' : transformedOject[object['item-code']]['__analytics'],
                'sub-type': transformedOject[object['item-code']]['sub-type']
            });
            this.productContentHelper.updateAnalyticsAsPerClassCustomisations(object);
            this.productContentHelper.updateAnalyticsAsPerClassCustomisations(transformedOject[object['item-code']]);
            transformedOject[object['item-code']].lchObject = object.lchObject = {
                iconObj: this.productContentHelper.getStatusIconForContent(object),
                scoresObj: this.productContentHelper.getFormattedScore(object)
            }
            if(this.contentAccessHelperService.getContentAccessEnabled()) {
                // creating deep copy of rules and merging lock and unlock rules into one
                const lockUnlockRules = this.contentAccessHelperService.mergeLockUnlockRules(JSON.parse(JSON.stringify(object.accessInfo.rules)));
                
                object['accessInfo']['finalResultantLockDetails'] = this.contentAccessHelperService.filterLockUnlockRules(this.contentAccessHelperService.getFinalResultantLockDetails(lockUnlockRules));
                transformedOject[object['item-code']]['accessInfo']['finalResultantLockDetails'] = object['accessInfo']['finalResultantLockDetails'];

                object['accessInfo']['isLocked'] = this.contentAccessHelperService.getCurrentActiveRule(object['accessInfo']['finalResultantLockDetails'], object['item-code'], this.productViewHelperService.getIsAssignmentOnlyProduct()).isLocked;
            }
        }
        return transformedOject;
    }

    getProductContentHelper(){
        return this.productContentHelper;
      }

    //Store Product State (Last launched item)
    updateAppState(productCode, appData, classId, isCBPClass, isTestTypeProduct) {
        let url = this.apiGatewayHelperService.getApiGatewayPrefixUrl(true) + productCode + '/appstate';
        if (classId) {
            url += '/' + classId;
            if (isCBPClass) url += '?isCBPClass=' + isCBPClass;
            if (isTestTypeProduct) url += '?isTestTypeProduct=' + isTestTypeProduct;
            return this.http.put(url, appData);
        } else {
            return this.http.put(url, appData);
        }
    }

    getAnalyticsOfItem(productCode, classId, itemId, groupId): Observable<any> {
        let url = this.apiGatewayHelperService.getApiGatewayPrefixUrl(true) + 'product/' + productCode + '/class/' + classId + '/item/' + itemId + '?history=true';
        if(groupId){
            url += '&groupId=' +  groupId;
        }
          return this.http.get(url).pipe(
              map(res => {
                return res;
              })
          ) 
    }

    isProductSuppressed(product, flag, spaceId) {

        // spaceid is of the form 'org_mqa-sierra-thor'.
        let isMQA = spaceId.indexOf("mqa") === 4;

        // check if org is mqa. if true show irrespective of visibility flag.
        // check if visibility is true, display if true.
        // product is hidden when org is non mqa and visibility is false.
        let isHidden = !isMQA && product && product['meta'] && product['meta']['visibility'] == 'false';

        // NEMO-6677 : suppress products with visibility hidden or visibility options false.
        if (isHidden || product && product['meta'] && product['meta']['visibility-options'] && product['meta']['visibility-options'][flag]) {
            return true;
        }
        return false;
        
    }

    /**
     * Add assignment start and due dates to the items map
     * - It merges the overlapping assignment dates for the same item code
     * 
     * Known limitation: This map stores only the details of the first non-overlapping assignment and it ignores other future assignments,
     * so if the user somehow stays beyond this stored assignment timeperiod, due to future asssignments being ignored, the user will see
     * the items as locked, if there exists a lock rule. But the item should ideally be accessible. 
     * 
     * @param itemsMap - Container of the assignment dates for each item code
     * @param itemCode - Item code
     * @param newStartDate - Start date of the new assignment
     * @param newDueDate - Due date of the new assignment
     */
    private addAssignmentDatesToItemsMap(itemsMap: any, itemCode: string, newStartDate: number, newDueDate: number) {
        if (!itemsMap[itemCode]) {
          itemsMap[itemCode] = { startDate: newStartDate, dueDate: newDueDate };
          return;
        }
    
        const { startDate, dueDate } = itemsMap[itemCode];
    
        if (startDate >= newStartDate) {
          // If the new start date is before the current start date
          itemsMap[itemCode].startDate = newStartDate;
    
          if (startDate > newDueDate) {
            // If start date is after new due date
            itemsMap[itemCode].dueDate = newDueDate;
          }
        }
        
        if (dueDate > newStartDate && dueDate <= newDueDate) {
          // If new start date is before current due date and new due date is after current due date
          itemsMap[itemCode].dueDate = newDueDate;
        }
      }
}